import React, { useState, useEffect } from 'react';
import { wikiAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Skeleton } from '../../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { BookOpen, Plus, Edit2, Trash2, Search } from 'lucide-react';
import { toast } from 'sonner';
import Markdown from 'react-markdown';

const CATEGORIES = ['general', 'editorial', 'style-guide', 'technical', 'hr', 'legal'];

export default function AdminWiki() {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ title: '', content: '', category: 'general' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await wikiAPI.list({ search: search || undefined });
      setPages(res.data.pages || []);
    } catch { toast.error('Failed to load wiki'); }
    finally { setLoading(false); }
  };

  const openPage = async (page) => {
    try {
      const r = await wikiAPI.get(page.id);
      setSelected(r.data);
      setEditing(false);
    } catch { toast.error('Failed to load page'); }
  };

  const openCreate = () => {
    setSelected(null);
    setForm({ title: '', content: '', category: 'general' });
    setEditing(true);
  };

  const openEdit = (page) => {
    setForm({ title: page.title || '', content: page.content || '', category: page.category || 'general' });
    setEditing(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Title required'); return; }
    setSaving(true);
    try {
      if (selected?.id) {
        const r = await wikiAPI.update(selected.id, form);
        setSelected(r.data);
        toast.success('Page updated');
      } else {
        await wikiAPI.create(form);
        toast.success('Page created');
        setEditing(false);
        setSelected(null);
      }
      load();
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this wiki page?')) return;
    try {
      await wikiAPI.delete(id);
      toast.success('Page deleted');
      if (selected?.id === id) setSelected(null);
      load();
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><BookOpen className="w-6 h-6 text-[#B89B5E]" /> Internal Wiki</h1>
          <p className="text-sm text-[#5B616E] mt-1">Newsroom knowledge base — style guides, processes, standards</p>
        </div>
        <Button onClick={openCreate} className="bg-[#121316] text-white hover:bg-[#1B1D22]">
          <Plus className="w-4 h-4 mr-2" /> New Page
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Page list */}
        <div className="space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5B616E]" />
            <Input className="pl-10" placeholder="Search pages..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          {loading ? (
            <div className="space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-16 rounded-xl" />)}</div>
          ) : pages.length === 0 ? (
            <div className="text-center py-8 text-[#5B616E] text-sm">No pages yet. Create the first one!</div>
          ) : pages.map(p => (
            <Card key={p.id} onClick={() => openPage(p)} className={`bg-white border-[#E7E0D6] cursor-pointer transition-colors hover:border-[#B89B5E] ${selected?.id === p.id ? 'border-[#B89B5E]' : ''}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{p.title}</p>
                    <Badge variant="outline" className="text-[10px] mt-1">{p.category}</Badge>
                  </div>
                  <button className="ml-2 text-red-500 hover:text-red-700 flex-shrink-0" onClick={e => { e.stopPropagation(); handleDelete(p.id); }}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Page content */}
        <div className="lg:col-span-2">
          {editing ? (
            <Card className="bg-white border-[#E7E0D6]">
              <CardHeader>
                <CardTitle className="text-sm">{selected ? 'Edit Page' : 'New Page'}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium mb-1 block">Title *</label>
                    <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Page title" />
                  </div>
                  <div>
                    <label className="text-xs font-medium mb-1 block">Category</label>
                    <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium mb-1 block">Content (Markdown)</label>
                  <Textarea rows={16} className="font-mono text-xs" value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))} placeholder="# Title\n\nWrite wiki content here (Markdown supported)..." />
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleSave} disabled={saving} className="bg-[#121316] text-white hover:bg-[#1B1D22]">{saving ? 'Saving…' : 'Save'}</Button>
                  <Button variant="ghost" onClick={() => { setEditing(false); }}>Cancel</Button>
                </div>
              </CardContent>
            </Card>
          ) : selected ? (
            <Card className="bg-white border-[#E7E0D6]">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>{selected.title}</CardTitle>
                    <Badge variant="outline" className="text-[10px] mt-1">{selected.category}</Badge>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => openEdit(selected)}>
                    <Edit2 className="w-4 h-4 mr-1" /> Edit
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="prose prose-sm max-w-none">
                  <Markdown>{selected.content || ''}</Markdown>
                </div>
                <p className="text-[10px] text-[#5B616E] mt-6">
                  Last updated: {selected.updated_at ? new Date(selected.updated_at).toLocaleString() : ''}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="flex items-center justify-center h-48 text-[#5B616E]">
              <div className="text-center">
                <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-20" />
                <p>Select a page or create a new one</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
